import React, { useRef, useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import Modal from './modal';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ModalZoomPan from './modal-zoom-pan';

const ImageEditor = ({
  image,
  points = [],
  selectedProduct,
  heading,
  showFullScreen = true,
  onPointSelect = () => {},
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="w-full h-full flex flex-col gap-6">
      <div className={showFullScreen ? 'w-full bg-white border border-[#D9D9D9] p-5' : 'w-full'}>
        {image && (
          <>
            {/* ---------- INLINE (unchanged) ---------- */}
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={3}
              wheel={{ step: 0.15 }}
              pinch={{ step: 0.15 }}
              doubleClick={{ disabled: true }}
              limitToBounds
              centerOnInit
            >
              {({ state, zoomIn, zoomOut }) => (
                <div className="relative">
                  {/* Zoom buttons */}
                  <div className="hidden md:flex absolute left-4 top-4 flex-col gap-2 z-50 pointer-events-auto">
                    <button
                      onClick={() => { try { zoomIn(0.4); } catch { zoomIn(); } }}
                      className="bg-white border border-black rounded-full p-2 shadow hover:bg-gray-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { try { zoomOut(0.4); } catch { zoomOut(); } }}
                      disabled={state?.scale <= 1}
                      className="bg-white border border-black rounded-full p-2 shadow hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Fullscreen trigger (desktop) */}
                  {showFullScreen && (
                    <div className="absolute right-4 bottom-4 hidden md:flex z-50 pointer-events-auto">
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex gap-2 items-center bg-[#F9F9F9] border border-black rounded-full px-3 py-2 shadow hover:bg-gray-50 font-bold text-sm"
                        type="button"
                        aria-label="Open fullscreen"
                      >
                        <img
                          alt=""
                          src="/images/full-screen-icon.png"
                          className="w-4 h-4"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        FULLSCREEN
                      </button>
                    </div>
                  )}

                  {/* Inline image + points */}
                  <TransformComponent
                    wrapperClass="overflow-hidden mt-4 h-full max-w-full"
                    contentClass="relative inline-block"
                  >
                    <div className="relative w-full flex justify-center">
                      <img
                        src={image?.preview || 'https://placehold.co/1200x1600/png'}
                        onLoad={() =>
                          window.parent.postMessage({ frameHeight: document.body.scrollHeight }, '*')
                        }
                        alt="Uploaded"
                        className="block max-w-full h-auto select-none"
                        draggable={false}
                      />

                      {points.filter(p => !p.is_hidden).map((point, index) => (
                        <div
                          key={index}
                          className="absolute cursor-pointer"
                          onClick={() => onPointSelect(point)}
                          style={{
                            left: `${point.x}%`,
                            top: `${point.y}%`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <div
                            className={`rounded-full border-2 flex items-center justify-center text-black font-bold border-green-500 ${
                              point.number === selectedProduct?.related_number ? 'shadow-md shadow-green-500' : ''
                            }`}
                            style={{ width: `${point.size}px`, height: `${point.size}px` }}
                          >
                            {point.number}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TransformComponent>
                </div>
              )}
            </TransformWrapper>

            {/* ---------- FULLSCREEN (pure pointer pan/zoom) ---------- */}
            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              heading={heading}
              mobileFullscreen
              bodyClassName="p-0 overflow-hidden"
            >
              <div className="w-full h-[calc(100dvh-70px)] md:h-[calc(100vh-70px)] overflow-hidden">
                <ModalZoomPan
                  src={image?.preview || 'https://placehold.co/1200x1600/png'}
                  points={points?.filter(p => !p.is_hidden) || []}
                  onPointSelect={onPointSelect}
                />
              </div>
            </Modal>
          </>
        )}
      </div>

      {/* Fullscreen trigger (mobile) */}
      {showFullScreen && (
        <div className="md:hidden mt-3">
          <button
            onClick={() => setIsModalOpen(true)}
            type="button"
            aria-label="Open fullscreen (mobile)"
            className="inline-flex gap-2 items-center bg-[#F9F9F9] border border-black rounded-full px-3 py-2 shadow hover:bg-gray-50 font-bold text-sm"
          >
            <img
              alt=""
              src="/images/full-screen-icon.png"
              className="w-4 h-4"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            FULLSCREEN
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
